import { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectGrossAnnualSales, selectPlLimit } from '../selectors/application';
import useGetApplicationOptions from './useGetApplicationOptions';
import { updateProfessionalLiability } from '../actions/application';

const usePLLimitOptions = () => {
  const dispatch = useDispatch();
  const occurrenceLimit = useSelector(selectPlLimit) ?? undefined;
  const grossAnnualSales = useSelector(selectGrossAnnualSales);

  const { data: applicationOptions, isError, isLoading } = useGetApplicationOptions();

  const occurrenceLimitOptions = useMemo(() => {
    if (isLoading || isError) {
      return [];
    }
    return (applicationOptions?.professionalLiability?.limits ?? []).map((l: PLLimitsOption) => l.occurrenceLimit);
  }, [applicationOptions, isError, isLoading]);

  const occurrenceToAggregateMap = useMemo((): Record<number, number> => {
    if (isLoading || isError || !applicationOptions?.professionalLiability?.limits) {
      return {};
    }
    return applicationOptions.professionalLiability.limits.reduce((acc: Record<number, number>, l: PLLimitsOption) => {
      acc[l.occurrenceLimit] = l.aggregateLimit;
      return acc;
    }, {} as Record<number, number>);
  }, [applicationOptions, isError, isLoading]);

  useEffect(() => {
    if (occurrenceLimitOptions.length > 0 && !occurrenceLimit) {
      const firstOption = occurrenceLimitOptions[0];
      dispatch(
        updateProfessionalLiability({
          grossRevenue: grossAnnualSales || 0,
          occurrenceLimit: firstOption,
          aggregateLimit: occurrenceToAggregateMap[firstOption],
        })
      );
    }
  }, [occurrenceLimitOptions, occurrenceLimit, dispatch, grossAnnualSales, occurrenceToAggregateMap]);

  return { occurrenceLimitOptions, occurrenceToAggregateMap, isLoading };
};

export default usePLLimitOptions;
